import React, { useState, useEffect } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { useAuth } from '../../../context/AuthContext';

export function PaymentModal({ payment, onClose, onSave, company }) {
  const { token } = useAuth();
  const realCompanies = ["GENERAL TIRE", "MIGUEL", "MAURICIO", "IMPORTADORA"];
  const categories = ["Agua","Alestra","AltaStockman","CFE","CHECK-PLUS","Gastos Variables","GoodYear","Govi","Honorarios","JK-Tornel","Maniobras KCS","Megacable","Rentas","Seguridad","Transportes","Telcel","TELMEX","Vacios","Otros"];

  const [formData, setFormData] = useState({
    company: company === 'TODAS' ? realCompanies[0] : company,
    title: '', category: categories[0], amount: '', currency: 'MXN',
    due_date: '', recurrence: 'none', status: 'pendiente', note: '',
    provider_email: '' 
  });
  const [invoicesFiles, setInvoicesFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (payment) {
      const formattedDate = payment.due_date && isValid(parseISO(payment.due_date))
        ? format(parseISO(payment.due_date), 'yyyy-MM-dd') : '';
      setFormData({
        company: payment.company,
        title: payment.title,
        category: payment.category,
        amount: payment.amount ?? '',
        currency: payment.currency || 'MXN',
        due_date: formattedDate,
        recurrence: payment.recurrence || 'none',
        status: payment.status || 'pendiente',
        note: payment.note || '',
        provider_email: payment.provider_email || ''
      });
    } else {
      setFormData(prev => ({ ...prev, company: company === 'TODAS' ? realCompanies[0] : company, due_date: format(new Date(), 'yyyy-MM-dd') }));
    }
  }, [payment, company]);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const uploadInvoices = async (paymentId) => {
    if (!invoicesFiles.length) return true;
    const fd = new FormData();
    for (const f of invoicesFiles) fd.append('invoices', f);
    const res = await fetch(`/api/owner/payments/${paymentId}/invoices`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: fd
    });
    if (!res.ok) {
      const d = await res.json().catch(()=>({}));
      throw new Error(d.mensaje || 'No se pudieron subir las facturas.');
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setMessage('');
    const isEdit = !!payment;
    const url = isEdit ? `/api/owner/payments/${payment.id}` : '/api/owner/payments';
    const method = isEdit ? 'PUT' : 'POST';
    const payload = { ...formData, amount: Number(formData.amount) };

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.mensaje || 'Ocurrió un error.');

      const paymentId = isEdit ? payment.id : data.id;
      if (paymentId) await uploadInvoices(paymentId);

      setMessage(isEdit ? 'Pago actualizado.' : 'Pago creado.');
      setTimeout(() => { onSave(); onClose(); }, 700);
    } catch (err) {
      setMessage(`Error: ${err.message || 'Ocurrió un error.'}`);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-white/10 bg-white shadow-2xl" onClick={(e)=>e.stopPropagation()}>
        <div className="bg-gradient-to-r from-slate-900 to-slate-700 px-6 py-5">
          <h3 className="text-lg md:text-xl font-semibold text-white">{payment ? 'Editar pago' : 'Nuevo pago'}</h3>
          <p className="text-slate-300 text-sm mt-1">Captura o edita un registro de pago.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-medium text-slate-600">Empresa</label>
              <select name="company" value={formData.company} onChange={handleChange}
                className="mt-1 w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/20"
                disabled={company !== 'TODAS'}>
                {realCompanies.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Factura</label>
              <input name="title" value={formData.title} onChange={handleChange} required
                className="mt-1 w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/20"/>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Categoría</label>
              <select name="category" value={formData.category} onChange={handleChange}
                className="mt-1 w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/20">
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Correo Proveedor</label>
              <input type="text" name="provider_email" value={formData.provider_email} onChange={handleChange} placeholder="ejemplo@proveedor.com"
                className="mt-1 w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/20"/>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Monto</label>
                <input type="number" step="0.01" name="amount" value={formData.amount} onChange={handleChange} required
                  className="mt-1 w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/20"/>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Moneda</label>
                <select name="currency" value={formData.currency} onChange={handleChange}
                  className="mt-1 w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/20">
                  <option>MXN</option><option>USD</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Vencimiento</label>
              <input type="date" name="due_date" value={formData.due_date} onChange={handleChange} required
                className="mt-1 w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/20"/>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Recurrencia</label>
              <select name="recurrence" value={formData.recurrence} onChange={handleChange}
                className="mt-1 w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/20">
                <option value="none">Ninguna</option>
                <option value="mensual">Mensual</option>
                <option value="trimestral">Trimestral</option>
                <option value="anual">Anual</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-600">Titulo/Observaciones</label>
              <textarea name="note" rows="3" value={formData.note} onChange={handleChange}
                className="mt-1 w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/20"/>
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-600">Facturas (PDF/JPG/PNG)</label>
              <input
                type="file"
                multiple
                accept="application/pdf,image/*"
                onChange={(e)=> setInvoicesFiles(Array.from(e.target.files||[]))}
                className="mt-1 block w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/20"
              />
              <div className="text-xs text-slate-500 mt-1">
                Seleccionadas: <b>{invoicesFiles.length}</b> (máx 20 por carga)
              </div>
            </div>
          </div>

          <div className="border-t bg-slate-50 px-6 py-4 flex items-center justify-between">
            <span className={`text-sm ${message.includes('Error') ? 'text-rose-600' : 'text-emerald-600'}`}>{message}</span>
            <div className="flex gap-3">
              <button type="button" onClick={onClose}
                className="rounded-xl px-4 py-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50">Cancelar</button>
              <button type="submit" disabled={loading}
                className="rounded-xl px-4 py-2 bg-slate-900 text-white shadow-lg hover:shadow-xl disabled:opacity-50">
                {loading ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
